import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, businessInfo } from '../data/mock';

const Services = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-teal/5 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-navy mb-6">
              Our Bookkeeping Services
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Comprehensive bookkeeping solutions tailored to your business needs. 
              From day-to-day transaction recording to complex financial reporting, we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <Card key={service.id} className="border border-slate-200 shadow-md hover:shadow-xl transition-all group hover:-translate-y-1">
                <CardContent className="p-8 lg:p-10">
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 rounded-2xl bg-teal/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal/20 transition-colors">
                      <span className="text-2xl font-bold text-teal">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-semibold text-navy mb-4 group-hover:text-teal transition-colors">
                        {service.title}
                      </h2>
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-3 text-slate-600">
                            <CheckCircle className="text-teal flex-shrink-0" size={18} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4">
              How We Work Together
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Getting started is simple. We've designed our process to be straightforward and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Free Consultation", description: "We discuss your business needs and answer any questions you have." },
              { step: "2", title: "Custom Quote", description: "You receive a clear, fixed-fee quote with no hidden surprises." },
              { step: "3", title: "Onboarding", description: "We securely set up access to your accounts and get to know your business." },
              { step: "4", title: "Ongoing Support", description: "Regular bookkeeping and support whenever you need it." }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber text-navy font-bold text-2xl flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Every business is different. Get in touch and we'll help you figure out exactly what support you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessInfo.phone}`}>
              <Button size="lg" className="bg-amber hover:bg-amber-dark text-navy font-semibold px-8 h-14 text-lg w-full sm:w-auto">
                Call: {businessInfo.phone}
              </Button>
            </a>
            <a href={`mailto:${businessInfo.email}?subject=Bookkeeping%20Enquiry&body=Hello%20Wonderman%20Bookkeeping%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20bookkeeping%20services.%0A%0AName%3A%20%0ABusiness%20Name%3A%20%0APhone%3A%20%0A%0APlease%20get%20in%20touch%20at%20your%20earliest%20convenience.%0A%0AThank%20you.`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy px-8 h-14 text-lg w-full sm:w-auto">
                Send a Message
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
